/**
 * Membership helpers (ORCH-336)
 *
 * Purpose: Names, the security-attribute summary and the before/after diff the
 *          User Manager shows for a tenant membership. Pure functions, shared by
 *          the list, the user page, the review step and My Access.
 */

import { asList } from '../../utils/listValues';
import { Member } from '../../types/userManagement';

export const MEMBERSHIP_MODEL_ID = 'user_tenant';
/** The membership's Company (ORCH-410 §4.2): who the person works for in the tenant. */
export const COMPANY_FIELD = 'fd_company';

type Named = Pick<Member, 'first_name' | 'last_name' | 'email'>;

/** "Last, First": the list's Name column, sorted by last name. */
export const lastFirst = (person: Named): string => {
  const last = (person.last_name || '').trim();
  const first = (person.first_name || '').trim();
  if (last && first) return `${last}, ${first}`;
  return last || first || person.email;
};

/** "First Last": titles, dialogs and greetings. */
export const firstLast = (person: Named): string =>
  [person.first_name, person.last_name]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ') || person.email;

/** What the person is called in a sentence: their first name, else their email. */
export const givenName = (person: Named): string =>
  (person.first_name || '').trim() || person.email;

/** Every security-attribute value id on a membership, Company excluded. */
export const attributeValues = (
  metadata: Record<string, unknown> | undefined
): { fielddefId: string; valueId: string }[] =>
  Object.entries(metadata || {})
    .filter(([fielddefId]) => fielddefId !== COMPANY_FIELD)
    .flatMap(([fielddefId, value]) => asList(value).map((valueId) => ({ fielddefId, valueId })));

/** A stored membership without blank entries, so "cleared" and "never set" compare equal. */
export const cleanMetadata = (metadata: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(metadata).filter(([, value]) => asList(value).length > 0));

export interface MembershipDraft {
  roleIds: string[];
  /** `user_tenant` fields by fielddef id. */
  metadata: Record<string, unknown>;
}

export interface MembershipChange {
  /** `updated` is one profile field's change, shown as "before → after" (ORCH-410). */
  kind: 'added' | 'removed' | 'updated';
  /** What changed: "Role", or the field's label ("Geography", "Company", "Country"). */
  label: string;
  /** The value added or removed; for `updated`, the value after the change. */
  value: string;
  /** For `updated`: the value before the change. */
  before?: string;
}

/** One profile field whose stored value differs from the saved one, in the form's shape. */
export interface ProfileFieldChange {
  fielddefId: string;
  before: unknown;
  after: unknown;
}

const NOT_SET = 'Not set';

/** One side of a profile field change: a list value by name, text as typed (ORCH-422). */
export const describeProfileValue = (
  field: string,
  value: unknown,
  names: Pick<DiffNames, 'valueName'>
): string =>
  value === null || value === undefined || value === ''
    ? NOT_SET
    : names.valueName(field, String(value));

/** A stored profile value for comparison: blank forms are alike, anything else as text. */
const storedText = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

/**
 * The profile fields whose stored value changed, each against its own saved value (ORCH-410)
 *
 * Logic: Both sides are the form's stored shape (`toStored`), so a following list
 *        value ('' on both sides) and a Phone holding only its prefilled calling code
 *        never show up. Fields come in `before`'s order (the form's), then any new ones.
 */
export const diffProfile = (
  before: Record<string, unknown>,
  after: Record<string, unknown>
): ProfileFieldChange[] =>
  Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
    .filter((fielddefId) => storedText(before[fielddefId]) !== storedText(after[fielddefId]))
    .map((fielddefId) => ({ fielddefId, before: before[fielddefId], after: after[fielddefId] }));

/** Profile field changes as the review step lists them: "Country: Not set → France". */
export const profileChanges = (
  changes: ProfileFieldChange[],
  names: Pick<DiffNames, 'fieldLabel' | 'valueName'>
): MembershipChange[] =>
  changes.map((change) => ({
    kind: 'updated' as const,
    label: names.fieldLabel(change.fielddefId),
    before: describeProfileValue(change.fielddefId, change.before, names),
    value: describeProfileValue(change.fielddefId, change.after, names),
  }));

interface DiffNames {
  roleName: (roleId: string) => string;
  fieldLabel: (fielddefId: string) => string;
  valueName: (fielddefId: string, valueId: string) => string;
}

/** Every role and attribute value added or removed between two states of a membership. */
export const diffMembership = (
  before: MembershipDraft,
  after: MembershipDraft,
  names: DiffNames
): MembershipChange[] => {
  const changes: MembershipChange[] = [];
  const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
  const push = (kind: MembershipChange['kind'], label: string, values: string[]) =>
    values.sort(byName).forEach((value) => changes.push({ kind, label, value }));

  push(
    'added',
    'Role',
    after.roleIds.filter((id) => !before.roleIds.includes(id)).map(names.roleName)
  );
  push(
    'removed',
    'Role',
    before.roleIds.filter((id) => !after.roleIds.includes(id)).map(names.roleName)
  );

  const fields = Array.from(
    new Set([...Object.keys(before.metadata), ...Object.keys(after.metadata)])
  );
  fields.forEach((fielddefId) => {
    const was = asList(before.metadata[fielddefId]);
    const now = asList(after.metadata[fielddefId]);
    const label = names.fieldLabel(fielddefId);
    push(
      'removed',
      label,
      was.filter((id) => !now.includes(id)).map((id) => names.valueName(fielddefId, id))
    );
    push(
      'added',
      label,
      now.filter((id) => !was.includes(id)).map((id) => names.valueName(fielddefId, id))
    );
  });
  return changes;
};

/** The metadata fields that differ between two states, for the save call. */
export const changedMetadata = (
  before: Record<string, unknown>,
  after: Record<string, unknown>
): Record<string, unknown> => {
  const changed: Record<string, unknown> = {};
  new Set([...Object.keys(before), ...Object.keys(after)]).forEach((key) => {
    const was = asList(before[key]).slice().sort();
    const now = asList(after[key]).slice().sort();
    if (was.join('|') !== now.join('|')) {
      // A cleared field is sent blank in its declared shape (a list stays a list)
      changed[key] = after[key] ?? (Array.isArray(before[key]) ? [] : null);
    }
  });
  return changed;
};

export const sameIds = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

interface AuditPerson {
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

/** A person record as formatAuditUser takes it (it wants absent, not null, names). */
export const auditPerson = (
  person: AuditPerson | null | undefined
): { email?: string; first_name?: string; last_name?: string } | undefined =>
  person
    ? {
        email: person.email ?? undefined,
        first_name: person.first_name ?? undefined,
        last_name: person.last_name ?? undefined,
      }
    : undefined;

/**
 * How the User Manager words a membership or account status: an inactive one is
 * "Deactivated" here (the action that made it so), and a pending one is "Invited".
 * Undefined leaves StatusChip's own label.
 */
export const membershipStatusLabel = (status: string): string | undefined =>
  ({ inactive: 'Deactivated', pending: 'Invited' })[status];
